import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { ToastProvider, useToast } from '@zillow/constellation-10';
import { RpErrorBoundary } from '@zg-rentals/rp-bootstrap-browser';

type AppToastProviderProps = {
  children: ReactNode;
};

/**
 * Scopes an error boundary to the toast portal so a toast-queue crash unmounts only the
 * toast UI instead of reaching the app-level boundary and replacing the whole page.
 * On error the queue is cleared and the boundary remounted so later toasts still display.
 */
function ResilientToastPortal() {
  const { dismissAll } = useToast();
  const [boundaryKey, setBoundaryKey] = useState(0);

  const handleError = useCallback(() => {
    dismissAll();
    setBoundaryKey((key) => key + 1);
  }, [dismissAll]);

  return (
    /* Empty fragment, not null — a nullish fallback renders RpErrorBoundary's default alert. */
    <RpErrorBoundary key={boundaryKey} onError={handleError} fallback={<></>}>
      <ToastProvider.Portal>
        <ToastProvider.Content>
          <ToastProvider.List />
        </ToastProvider.Content>
      </ToastProvider.Portal>
    </RpErrorBoundary>
  );
}

export default function AppToastProvider({ children }: AppToastProviderProps) {
  return (
    <ToastProvider.Root placement="bottom-end">
      {children}
      <ResilientToastPortal />
    </ToastProvider.Root>
  );
}
